<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">Advertising Management</h5>

    <!-- Filter box -->
    <div class="container-fluid bg-gray q-mb-md overflow-auto">
      <div class="row items-center q-col-gutter-md q-pa-md">
        <div class="col-8 col-lg-6 col-xl-4 q-gutter-md">
          <q-btn outline="outline" color="primary" icon="cached" label="reset" @click="reset" />
          <q-btn color="primary" icon="add" label="new advert" @click="editDialogOpen = true" />
        </div>
        <div class="col-lg-6 col-xl-4">
          <q-input v-model="search" dense label="Search" hint="Search by company name" @keypress.enter="searchAd" />
        </div>
        <div class="col-4 offset-8 col-lg-3 offset-lg-9 col-xl-2 offset-xl-0">
          <q-btn color="primary" label="Search" class="full-width" icon="search" @click="searchAd" />
        </div>
      </div>
    </div>

    <!-- Datatable -->
    <DataTable :columns="columns">
      <md-table-row v-if="!adverts.length">
        <md-table-cell colspan="7" class="text-center">
          <span v-if="search">No advertisements found.</span>
          <span v-else>There are no advertisements yet.</span>
        </md-table-cell>
      </md-table-row>

      <md-table-row v-for="advert in adverts" :key="advert.id">
        <md-table-cell md-numeric="md-numeric">{{ advert.id }}</md-table-cell>
        <md-table-cell>{{ advert.company.tradingName | uppercase }}</md-table-cell>
        <md-table-cell class="ad-info">{{ advert.title | truncate(5) }}</md-table-cell>
        <md-table-cell class="ad-info">
          {{ advert.second | truncate(5) }}
        </md-table-cell>
        <md-table-cell class="text-weight-medium" :class="advert.isActive ? 'text-positive' : 'text-negative'">
          {{ advert.isActive | formatStatus }}
        </md-table-cell>
        <md-table-cell>{{ advert.createdAt | formatDateTime }}</md-table-cell>
        <md-table-cell>
          <span class="text-action q-mr-md" @click="openEditDialogChange(advert)"> Change </span>
          <span class="text-action text-negative" @click="openDeleteDialog(advert)"> Delete </span>
        </md-table-cell>
      </md-table-row>
    </DataTable>

    <Pagination v-show="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <EditAdDialog
      :is-open.sync="editDialogOpen"
      :advert.sync="selectedAd"
      :on-create-success="handleCreateSuccess"
      :on-edit-success="handleEditSuccess"
    />

    <DeleteAdDialog
      :is-open.sync="deleteDialogOpen"
      :advert.sync="selectedAd"
      :on-delete-success="handleDeleteSuccess"
    />
  </Wrapper>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import debounce from 'lodash/debounce';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTable.vue';
import Pagination from '@/components/Pagination.vue';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import EditAdDialog from './AdManagement/EditAdDialog.vue';
import DeleteAdDialog from './AdManagement/DeleteAdDialog.vue';

export default {
  components: {
    Wrapper,
    DataTable,
    EditAdDialog,
    DeleteAdDialog,
    Pagination,
  },

  filters: {
    formatStatus(val) {
      return val ? 'Active' : 'Inactive';
    },
  },

  mixins: [formatDateMixin],

  data() {
    return {
      adverts: [],
      editDialogOpen: false,
      deleteDialogOpen: false,
      selectedAd: undefined,
      currentPage: 1,
      totalPages: 0,
      search: '',
    };
  },

  computed: {
    ...mapFields(['snackbar']),
    columns() {
      return [
        {
          name: 'no',
          isNumeric: true,
        },
        'company',
        'title',
        'second line',
        'status',
        'created time',
        'operations',
      ];
    },
  },

  watch: {
    currentPage() {
      this.debouncedFetchData();
    },
  },

  mounted() {
    this.fetchData();
    this.debouncedFetchData = debounce(this.fetchData, 500);
  },

  methods: {
    getParams() {
      const params = {};

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      if (this.search) {
        params.search = this.search;
      }

      return params;
    },
    async fetchData() {
      const params = this.getParams();
      const {
        data: { data, pagination },
      } = await this.$axios.get('/admin/ad', { params });
      this.adverts = data;
      this.currentPage = pagination.page;
      this.totalPages = pagination.pages;
    },
    debouncedFetchData() {},
    reset() {
      if (!this.search && this.currentPage === 1) {
        return;
      }

      this.search = '';
      this.currentPage = 1;
    },
    searchAd() {
      if (this.currentPage > 1) {
        this.currentPage = 1;
      } else {
        this.debouncedFetchData();
      }
    },
    openEditDialogChange(ad) {
      this.editDialogOpen = true;
      this.selectedAd = ad;
    },
    handleCreateSuccess(newAdvert) {
      this.adverts.unshift(newAdvert);
      this.snackbar.isOpen = true;
      this.snackbar.message = 'A new advertisement has been created!';
    },
    handleEditSuccess(advert) {
      const existingIndex = this.adverts.findIndex((ad) => ad.id === advert.id);
      this.adverts.splice(existingIndex, 1, advert);
      this.snackbar.isOpen = true;
      this.snackbar.message = 'An advertisement has been edited!';
    },
    openDeleteDialog(advert) {
      this.deleteDialogOpen = true;
      this.selectedAd = advert;
    },
    handleDeleteSuccess() {
      const existingIndex = this.adverts.findIndex((ad) => ad.id === this.selectedAd.id);

      if (this.currentPage > 1 && existingIndex === 0) {
        this.currentPage -= 1;
      } else {
        this.adverts.splice(existingIndex, 1);
      }

      this.snackbar.isOpen = true;
      this.snackbar.message = 'An advertisement has been deleted!';
    },
  },
};
</script>

<style lang="scss">
#admin-system-ad-management {
  .ad-info {
    .md-table-cell-container {
      padding: 0.5rem;
    }
  }
}
</style>
